import { Scene, SceneActivationParams, SceneMountParams, SceneParams } from "./Graphic";
import {fromEvent, Subscription, animationFrameScheduler, interval, BehaviorSubject, Observable, of, zip} from "rxjs";
import {
    observeOn,
    map,
    withLatestFrom,
    throttleTime,
    distinctUntilChanged,
    switchMap,
    debounceTime, bufferCount,
} from "rxjs/operators";
import { interpolateRound } from "d3-interpolate";
import {fromArray} from "rxjs/internal/observable/fromArray";
import {scaleLinear} from "d3-scale";

export type VideoParams = {
    framerate: number;
    vidEl: HTMLVideoElement;
    frameSteps: number;
    // steps: VidStepData[];
    numFrames: number;
}

export type VideoSceneParams = SceneParams & VideoParams;

export class VideoScene implements Scene {
    public screenLengths = 0;
    public graphicContainer: HTMLDivElement;
    public textContainer: HTMLDivElement;

    private vidEl: HTMLVideoElement;
    private framerate: number;
    private frameSteps: number;
    // private steps: VidStepData[];


    private tick$ = interval(0, animationFrameScheduler);
    private lastDrawn$ = new BehaviorSubject<number>(0);
    private targetFrame$ = new BehaviorSubject<number>(0);
    private framesToDraw$ = new BehaviorSubject<number[]>([]);
    private lastToDraw$ = new BehaviorSubject<number[]>([]);
    private drain$ = new BehaviorSubject<number>(0)
    private remainder$ = new BehaviorSubject<number[]>([]);
    private timeupdate$: Observable<number>;
    // private activeSteps = [];
    private numFrames: number;
    private isMounted = false;



    constructor(p: VideoSceneParams) {
        this.vidEl = p.vidEl
        this.framerate = p.framerate;
        this.screenLengths = p.screenLengths;
        this.frameSteps = p.frameSteps;
        // this.steps = p.steps;
        this.numFrames = p.numFrames;


        this.timeupdate$ = fromEvent(this.vidEl, "timeupdate")

        this.graphicContainer = document.createElement("div")
        this.textContainer = document.createElement("div")
        this.graphicContainer.classList.add("vid-container")
        this.graphicContainer.classList.add((<any>window).isMobile.any ? "mobile-vid" : "desktop-vid")
        for (let el of [this.graphicContainer, this.textContainer]) {
            el.style.width = "100%";
        }
        this.graphicContainer.append(this.vidEl);
        // this.initText();
    }

    activate(params: SceneActivationParams): Subscription[] {
        let subs = [];

        let timesToDraw$ = params.progress$.pipe(
            throttleTime(100),
            distinctUntilChanged(),
            withLatestFrom(this.lastDrawn$),
            switchMap(([scrollPct, lastDrawn]) => {
                const targetFrame = this.numFrames * scrollPct;
                const frameDiff = (targetFrame - lastDrawn) / this.frameSteps;
                const times = []
                for (let i = 1; i < this.frameSteps + 1; i++) {
                    times.push(lastDrawn + (i * frameDiff))
                }
                return of(times);
            })
        )

        this.timeupdate$.pipe(
            debounceTime(20),
            withLatestFrom(timesToDraw$, this.lastToDraw$, this.remainder$)
        ).subscribe(([_, t, l, r]) => {
            // If t and l are the same, continue to drain r
            if (JSON.stringify(t) === JSON.stringify(l)) {
                const d = r.shift();
                this.drain$.next(d);
                // If remainders is drained, repopulate with last value so that "timeupdate" keeps ticking
                this.remainder$.next(r);
                // Else, set l = t, r = t, and drain the first of t
            } else {
                this.remainder$.next(t.slice(1));
                this.lastToDraw$.next(t);
                this.drain$.next(t[0]);
            }
        })

        this.drain$.subscribe(this.update.bind(this))

        // subs.push(params.screenHeight$.subscribe(s => this.adjustStepHeight(s)))
        // let targetFrame$ = params.progress$.pipe(
        //     // throttleTime(500),
        //     distinctUntilChanged(),
        //     map((p) => {
        //         // console.log(Math.floor(this.numFrames * p))
        //         return Math.floor(this.numFrames * p)
        //     })
        // )
        //
        subs.push(params.progress$.pipe(
            throttleTime(1000)
        ).subscribe(_ => this.vidEl.dispatchEvent(new Event("timeupdate"))))
        //
        // let interpFrames$ = targetFrame$.pipe(
        //     withLatestFrom(this.lastDrawn$),
        //     map(([tf, ld]) => {
        //         let p = performance.now()
        //         return {
        //             interp: interpolateRound(ld, tf),
        //             start: p,
        //             deadline: p + 1000
        //         }
        //     })
        // )
        //
        // subs.push(this.timeupdate$.pipe(
        //     withLatestFrom(interpFrames$)
        // ).subscribe(([_, {interp, start, deadline}]) => {
        //     setTimeout(() => {
        //         let t = scaleLinear().domain([start, deadline]).range([0, 1])(performance.now())
        //         if (t <= 1) {
        //             let td = interp(t)
        //             // console.log(td)
        //             this.vidEl.currentTime = td / this.framerate;
        //             this.lastDrawn$.next(td);
        //         } else {
        //             let td = interp(1)
        //             // console.log(td)
        //             this.vidEl.currentTime = td / this.framerate;
        //             this.lastDrawn$.next(td)
        //         }
        //
        //     })
        // }))
        // setTimeout(() => this.vidEl.dispatchEvent(new Event("timeupdate")), 100)

        // setTimeout(() => this.vidEl.dispatchEvent(new Event("timeupdate")))


        return subs;
    }

    private update(t) {
        if (t && t !== this.vidEl.currentTime) {
            this.vidEl.currentTime = t / this.framerate;
            this.lastDrawn$.next(t);
        } else {
            setTimeout(() => this.vidEl.dispatchEvent(new Event("timeupdate")))
        }
    }

    private calculateNextFrame({tf, ld}) {
        let forward = tf > ld
        let diff = forward ? tf - ld : ld - tf;
        // if (diff % 5 === 0) {
        //     return ld + (forward ? 1 : -1) * 5
        // }
        // if (diff % 4 === 0) {
        //     return ld + (forward ? 1 : -1) * 4
        // }
        // if (diff % 3 === 0) {
        //     return ld + (forward ? 1 : -1) * 3
        // }
        // if (diff % 2 === 0) {
        //     return ld + (forward ? 1 : -1) * 2
        // }
        return ld + (forward ? 1 : -1) * Math.floor(diff > this.frameSteps? diff / this.frameSteps : 1)
    }

    public mount(params: SceneMountParams): void {
        if (!this.isMounted) {
            params.vizPlate.append(this.vidEl)
            this.vidEl.pause();
        }
        this.isMounted = true;
        // this.vidEl.play()
    }

    public deactivate(subs: Subscription[]) {
        for (let sub of subs) {
            sub.unsubscribe();
        }
    }

    // private adjustStepHeight(x: number) {
    //     for (let step of this.activeSteps) {
    //         step.el.style.top = `${x * step.screenLengthPos + (x * .5)}px`
    //     }
    // }

    // private initText() {
    //     for (let step of this.steps) {
    //         const p = document.createElement(step.elType || "p")
    //         p.classList.add("stepper-text")
    //         p.innerHTML = step.text;
    //         const d = document.createElement("div")
    //         d.classList.add("step")
    //         d.append(p)
    //         this.textContainer.append(d)
    //         this.activeSteps.push({
    //             el: d,
    //             screenLengthPos: step.screenLengthPos
    //         })
    //     }
    // }
}